import { useLicence } from '@/features/licence/hooks/useLicence'
import { DeliveryTypeCategory } from '@/features/orders/constants/app'
import {
    useFormNameField,
    useFormPhoneField,
    useFormReset,
    useFormSubmit,
} from '@/features/pos/components/CustomerProfile/form'
import { useCustomerLookupAddressState } from '@/features/pos/components/CustomerProfile/hooks/useCustomerLookupAddressState'
import { useCustomerLookupCustomers } from '@/features/pos/components/CustomerProfile/hooks/useCustomerLookupCustomers'
import {
    CustomerLookupAddressSection,
    CustomerLookupCustomer,
    CustomerLookupQueryVariables,
    CustomerLookupSaveInput,
    RightPanelState,
} from '@/features/pos/components/CustomerProfile/types'
import {
    CUSTOMER_SEARCH_DEBOUNCE_MS,
    canResetCustomerLookupState,
    getCustomerLookupPhone,
    getCustomerLookupRightPanelState,
    getCustomerSearchVariables,
    isAddressRequired,
    isPersistedCustomerLookupState,
} from '@/features/pos/components/CustomerProfile/utils'
import {
    transformPhoneSearchValueToProfilePhone,
    transformProfileFormToCustomerLookupSaveInput,
} from '@/features/pos/components/CustomerProfile/utils/transforms'
import { CustomerInput } from '@/features/pos/components/OrderFinalization/form/types'
import { useEffect, useMemo, useRef, useState } from 'react'
import { TextInput } from 'react-native'
import { useDebounce } from 'use-debounce'

interface UseCustomerLookupModalStateArgs {
    draftDeliveryTypeCategory: DeliveryTypeCategory
    initialCustomer?: CustomerInput
    onResetDeliveryTypeCategory: () => void
    onSave: (input: CustomerLookupSaveInput) => void
}

interface UseCustomerLookupModalStateResult {
    addressInputRef: React.RefObject<TextInput | null>
    addressQuery: string
    addressSections: CustomerLookupAddressSection[]
    customerResults: CustomerLookupCustomer[]
    customerSearchDialCode: string
    customerSearchPhone?: string
    handleAddressQueryChange: (value: string) => void
    handleAddressSelect: (addressId: string) => Promise<void>
    handleCustomerSelect: (customer: CustomerLookupCustomer) => Promise<void>
    handleReset: () => void
    handleSave: () => void
    name: string
    nameInputRef: React.RefObject<TextInput | null>
    phone: string
    phoneInputRef: React.RefObject<TextInput | null>
    requiredAddress: boolean
    rightPanelState: RightPanelState
    searchNewCustomerDisabled: boolean
    setName: (value: string) => void
    setPhone: (value: string) => void
}

interface SelectedCustomerState {
    customer: CustomerLookupCustomer
    name: string
    phone: string
}

export const useCustomerLookupModalState = ({
    draftDeliveryTypeCategory,
    initialCustomer,
    onResetDeliveryTypeCategory,
    onSave,
}: UseCustomerLookupModalStateArgs): UseCustomerLookupModalStateResult => {
    const { country } = useLicence()
    const [phoneField, setPhoneField] = useFormPhoneField()
    const [name, setName] = useFormNameField()
    const resetForm = useFormReset()
    const [selectedCustomerState, setSelectedCustomerState] = useState<SelectedCustomerState>()
    const selectedCustomer = selectedCustomerState?.customer
    const phoneInputRef = useRef<TextInput>(null)
    const nameInputRef = useRef<TextInput>(null)
    const phone = phoneField.number
    const [debouncedPhone] = useDebounce(phone, CUSTOMER_SEARCH_DEBOUNCE_MS)
    const [debouncedName] = useDebounce(name, CUSTOMER_SEARCH_DEBOUNCE_MS)
    const requiredAddress = isAddressRequired(draftDeliveryTypeCategory)
    const {
        addressInputRef,
        addressQuery,
        addressSections,
        handleAddressQueryChange,
        handleAddressSelect,
        handleCustomerAddressSelect,
        hasAddressSearch,
        resetAddressLookup,
        selectedAddress,
    } = useCustomerLookupAddressState({ requiredAddress, selectedCustomer })

    const customerSearchVariables = useMemo<CustomerLookupQueryVariables | undefined>(() => {
        return getCustomerSearchVariables(debouncedPhone, debouncedName)
    }, [debouncedName, debouncedPhone])

    const customerResults = useCustomerLookupCustomers({ variables: customerSearchVariables })

    useEffect(() => {
        if (!selectedCustomerState) return

        if (phone !== selectedCustomerState.phone || name !== selectedCustomerState.name) {
            setSelectedCustomerState(undefined)
        }
    }, [name, phone, selectedCustomerState])

    const isPersistedCustomerState = useMemo(() => {
        return isPersistedCustomerLookupState({
            addressQuery,
            hasAddressSearch,
            initialCustomer,
            name,
            phone,
            selectedCustomer,
        })
    }, [addressQuery, hasAddressSearch, initialCustomer, name, phone, selectedCustomer])

    const rightPanelState = useMemo<RightPanelState>(() => {
        return getCustomerLookupRightPanelState({
            addressQuery,
            customerSearchVariables,
            hasAddressSearch,
            isPersistedCustomerState,
            name,
            phone,
            requiredAddress,
            selectedCustomer,
        })
    }, [
        addressQuery,
        customerSearchVariables,
        hasAddressSearch,
        isPersistedCustomerState,
        name,
        phone,
        requiredAddress,
        selectedCustomer,
    ])

    const searchNewCustomerDisabled = !canResetCustomerLookupState({
        addressQuery,
        isPersistedCustomerState,
        name,
        phone,
        selectedAddress,
        selectedCustomer,
    })

    const handleCustomerSelect = async (customer: CustomerLookupCustomer) => {
        const selectedPhone = getCustomerLookupPhone(customer, phone, phoneField.dialCode)
        const selectedName = customer.name || customer.names[0] || ''

        setSelectedCustomerState({ customer, name: selectedName, phone: selectedPhone })
        if (phone !== selectedPhone) {
            setPhoneField(transformPhoneSearchValueToProfilePhone(selectedPhone, country))
        }
        setName(selectedName)
        await handleCustomerAddressSelect(customer)
    }

    const handlePhoneChange = (value: string) => {
        setPhoneField(transformPhoneSearchValueToProfilePhone(value, country))
    }

    const handleSave = useFormSubmit((form) => {
        onSave(transformProfileFormToCustomerLookupSaveInput(form, draftDeliveryTypeCategory))
    })

    const handleReset = () => {
        resetAddressLookup()
        resetForm()
        onResetDeliveryTypeCategory()
        setSelectedCustomerState(undefined)
    }

    return {
        addressInputRef,
        addressQuery,
        addressSections,
        customerResults,
        customerSearchDialCode: phoneField.dialCode,
        customerSearchPhone: customerSearchVariables?.phoneNumber ?? undefined,
        handleAddressQueryChange,
        handleAddressSelect,
        handleCustomerSelect,
        handleReset,
        handleSave,
        name,
        nameInputRef,
        phone,
        phoneInputRef,
        requiredAddress,
        rightPanelState,
        searchNewCustomerDisabled,
        setName,
        setPhone: handlePhoneChange,
    }
}
